import React, { useState, useEffect, useRef } from 'react';
import useSocketReconnect from '../../utils/useSocketReconnect';
import './MerxandatgeStockModal.css';

// Typical size order for sorting
const TYPICAL_SIZES = ['XS', 'S', 'M', 'L', 'XL', 'XXL'];

// Helper function to sort sizes according to typical order
const sortSizes = (sizes) => {
  if (!sizes || sizes.length === 0) return [];
  
  return [...sizes].sort((a, b) => {
    const indexA = TYPICAL_SIZES.indexOf(a.toUpperCase());
    const indexB = TYPICAL_SIZES.indexOf(b.toUpperCase());
    
    // If both are in the typical list, sort by their position
    if (indexA !== -1 && indexB !== -1) {
      return indexA - indexB;
    }
    // If only A is in the list, it comes first
    if (indexA !== -1) return -1;
    // If only B is in the list, it comes first
    if (indexB !== -1) return 1;
    // If neither is in the list, sort alphabetically
    return a.localeCompare(b);
  });
};

function MerxandatgeStockModal({ socket, item, onClose }) {
  const loadedItemId = useRef(null);
  useSocketReconnect(socket, () => {
    if (item?.id && loadedItemId.current !== item.id) socket.emit('.get_merxandatge_stock', item.id);
  });
  const [stockData, setStockData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [variantToDelete, setVariantToDelete] = useState(null);

  useEffect(() => {
    // Load detailed stock information
    if (item && item.id) {
      loadedItemId.current = null;
      
      const onMerxandatgeStock = (data) => {
        if (data.itemId === item.id) {
          loadedItemId.current = item.id;
          setStockData(data.stock || []);
          setLoading(false);
        }
      };
      socket.on('.merxandatge_stock', onMerxandatgeStock);
      socket.emit('.get_merxandatge_stock', item.id);
      
      return () => {
        socket.off('.merxandatge_stock', onMerxandatgeStock);
      };
    }
  }, [item, socket]);

  const handleStockChange = (variantId, field, value) => {
    const updatedStock = stockData.map(variant => {
      if (variant.id === variantId) {
        return { ...variant, [field]: value };
      }
      return variant;
    });
    
    setStockData(updatedStock);
  };

  const handleAddVariant = () => {
    // Find available sizes and colors
    const sizes = item.sizes || [];
    const colors = item.colors || [];
    
    // If no sizes or colors, just add a generic variant
    if (sizes.length === 0 && colors.length === 0) {
      const newVariant = {
        id: `temp-${Date.now()}`,
        size: '',
        color: '',
        stock: 0,
        isNew: true
      };
      
      setStockData([...stockData, newVariant]);
      return;
    }
    
    // Default to first size and color
    const newVariant = {
      id: `temp-${Date.now()}`,
      size: sizes.length > 0 ? sizes[0] : '',
      color: colors.length > 0 ? colors[0] : '',
      stock: 0,
      isNew: true
    };
    
    setStockData([...stockData, newVariant]);
  };

  const checkDuplicateVariant = () => {
    // Check for duplicate size/color combinations
    const variantMap = new Map();
    let hasDuplicates = false;
    
    stockData.forEach(variant => {
      const key = `${variant.size || 'none'}-${variant.color || 'none'}`;
      if (variantMap.has(key)) {
        hasDuplicates = true;
      } else {
        variantMap.set(key, true);
      }
    });
    
    return hasDuplicates;
  };

  const handleConfirmDelete = (variantId) => {
    setVariantToDelete(variantId);
  };

  const handleRemoveVariant = () => {
    if (!variantToDelete) return;
    
    const updatedStock = stockData.filter(variant => variant.id !== variantToDelete);
    setStockData(updatedStock);
    setVariantToDelete(null);
  };

  const cancelDelete = () => {
    setVariantToDelete(null);
  };

  const handleSaveStock = () => {
    // Validate that stock values are valid numbers
    for (const variant of stockData) {
      if (isNaN(parseInt(variant.stock)) || parseInt(variant.stock) < 0) {
        setError("L'estoc ha de ser un número no negatiu");
        return;
      }
    }
    
    // Check for duplicate variants
    if (checkDuplicateVariant()) {
      setError("Hi ha variants duplicades amb la mateixa talla i color");
      return;
    }
    
    setLoading(true);
    setError(null);
    
    // Update stock information
    socket.emit('.update_merxandatge_stock', {
      itemId: item.id,
      stock: stockData
    });
    
    socket.once('.merxandatge_stock_updated', (response) => {
      setLoading(false);
      
      if (response.success) {
        setSuccessMessage("Estoc actualitzat correctament");
        
        // Trigger data reload
        socket.emit('.get_all_merxandatge');
        
        // Also reload the current item's stock data
        socket.emit('.get_merxandatge_stock', item.id);
        
        setTimeout(() => {
          setSuccessMessage(null);
        }, 3000);
      } else {
        setError(response.message || "Error actualitzant l'estoc");
      }
    });
  };

  // Sort variants by size (and color if applicable)
  const getSortedVariants = () => {
    if (!stockData || stockData.length === 0) return [];
    
    return [...stockData].sort((a, b) => {
      // If item has sizes, sort by size first
      if (item.sizes && item.sizes.length > 0) {
        const sizeA = a.size || '';
        const sizeB = b.size || '';
        
        const indexA = TYPICAL_SIZES.indexOf(sizeA.toUpperCase());
        const indexB = TYPICAL_SIZES.indexOf(sizeB.toUpperCase());
        
        // If both are in the typical list, sort by their position
        if (indexA !== -1 && indexB !== -1) {
          if (indexA !== indexB) {
            return indexA - indexB;
          }
        } else if (indexA !== -1) {
          return -1;
        } else if (indexB !== -1) {
          return 1;
        } else {
          // Neither in typical list, sort alphabetically
          const sizeCompare = sizeA.localeCompare(sizeB);
          if (sizeCompare !== 0) return sizeCompare;
        }
      }
      
      // If item has colors, sort by color as secondary sort
      if (item.colors && item.colors.length > 0) {
        const colorA = a.color || '';
        const colorB = b.color || '';
        return colorA.localeCompare(colorB);
      }
      
      return 0;
    });
  };

  return (
    <div className="stock-modal-overlay">
      <div className="stock-modal-container">
        <button className="close-button" onClick={onClose} aria-label="Tancar">×</button>

        <h2>Gestionar estoc: {item.name}</h2>
        
        {error && (
          <div className="stock-error-message">
            <i className="fas fa-exclamation-circle"></i> {error}
          </div>
        )}
        
        {successMessage && (
          <div className="stock-success-message">
            <i className="fas fa-check-circle"></i> {successMessage}
          </div>
        )}
        
        {loading ? (
          <div className="loading-spinner">
            <div className="spinner"></div>
            <span>Carregant...</span>
          </div>
        ) : (
          <>
            <div className="stock-table-container">
              <table className="stock-table">
                <thead>
                  <tr>
                    {item.sizes && item.sizes.length > 0 && <th>Talla</th>}
                    {item.colors && item.colors.length > 0 && <th>Color</th>}
                    <th>Estoc</th>
                    <th>Preu (€)</th>
                    <th>Accions</th>
                  </tr>
                </thead>
                <tbody>
                  {stockData.length === 0 ? (
                    <tr>
                      <td colSpan={(item.sizes && item.sizes.length > 0 ? 1 : 0) +
                                  (item.colors && item.colors.length > 0 ? 1 : 0) + 3} 
                          className="no-variants">
                        No hi ha variants definides
                      </td>
                    </tr>
                  ) : (
                    getSortedVariants().map(variant => {
                      const stockLevel = parseInt(variant.stock) || 0;
                      let inputClass = 'stock-input';
                      if (stockLevel === 0) {
                        inputClass = 'stock-input stock-input-empty';
                      } else if (stockLevel <= 3) {
                        inputClass = 'stock-input stock-input-low';
                      }
                      const rowClass = variantToDelete === variant.id ? 'highlight-delete' : '';
                      return (
                      <tr key={variant.id} className={rowClass}>
                        {item.sizes && item.sizes.length > 0 && (
                          <td>
                            <select 
                              value={variant.size || ''} 
                              onChange={(e) => handleStockChange(variant.id, 'size', e.target.value)}
                              className="variant-select"
                            >
                              <option value="">Sense talla</option>
                              {sortSizes(item.sizes).map(size => (
                                <option key={size} value={size}>{size}</option>
                              ))}
                            </select>
                          </td>
                        )}
                        
                        {item.colors && item.colors.length > 0 && (
                          <td>
                            <select 
                              value={variant.color || ''} 
                              onChange={(e) => handleStockChange(variant.id, 'color', e.target.value)}
                              className="variant-select"
                            >
                              <option value="">Sense color</option>
                              {item.colors.map(color => (
                                <option key={color} value={color}>{color}</option>
                              ))}
                            </select>
                          </td>
                        )}
                        
                        <td>
                          <div className="stock-input-container">
                            <button 
                              className="stock-adjust-button"
                              onClick={() => handleStockChange(variant.id, 'stock', Math.max(0, parseInt(variant.stock) - 1))}
                              disabled={variant.stock <= 0}
                            >
                              −
                            </button>
                            <input 
                              type="number" 
                              min="0" 
                              value={variant.stock} 
                              onWheel={(event) => event.currentTarget.blur()}
                              onChange={(e) => handleStockChange(variant.id, 'stock', parseInt(e.target.value) || 0)}
                              className={inputClass}
                            />
                            <button 
                              className="stock-adjust-button"
                              onClick={() => handleStockChange(variant.id, 'stock', parseInt(variant.stock) + 1)}
                            >
                              +
                            </button>
                          </div>
                        </td>

                        <td>
                          <input
                            type="text"
                            value={variant.price !== null && variant.price !== undefined ? variant.price.toString().replace('.', ',') : ''}
                            onChange={(e) => {
                              const val = e.target.value;
                              const priceRegex = /^[0-9]*,?[0-9]*$/;
                              if (val === '' || priceRegex.test(val)) {
                                handleStockChange(variant.id, 'price', val === '' ? null : val.replace(',', '.'));
                              }
                            }}
                            placeholder={item.price ? item.price.toString().replace('.', ',') : '—'}
                            className="stock-input"
                            style={{ width: '80px' }}
                          />
                        </td>

                        <td>
                          {variantToDelete === variant.id ? (
                            <div className="confirm-buttons">
                              <button 
                                className="confirm-delete-button" 
                                onClick={handleRemoveVariant}
                              >
                                Confirmar
                              </button>
                              <button 
                                className="cancel-delete-button" 
                                onClick={cancelDelete}
                              >
                                Cancel·lar
                              </button>
                            </div>
                          ) : (
                            <button 
                              className="remove-variant-button" 
                              onClick={() => handleConfirmDelete(variant.id)}
                            >
                              Eliminar
                            </button>
                          )}
                        </td>
                      </tr>
                    );})
                  )}
                </tbody>
              </table>
            </div>
            
            <div className="stock-actions">
              <button 
                className="add-variant-button" 
                onClick={handleAddVariant}
              >
                <i className="fas fa-plus"></i> Afegir variant
              </button>

              <button 
                className="save-stock-button" 
                onClick={handleSaveStock}
                disabled={loading}
              >
                {loading ? 'Guardant...' : 'Guardar canvis'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default MerxandatgeStockModal;
